(ns ui.cli.guide
  "The content of the Storrito CLI page: install commands, the steps to
   a first draft, the coding-agent setup and the unattended variant.
   Pure data and functions, so `ui.cli.page` stays a thin layout and the
   content can be tested."
  (:require [clojure.string :as str]))

(def install-unix
  "curl -fsSL https://storrito.com/install.sh | sh")

(def install-windows
  "irm https://storrito.com/install.ps1 | iex")

(def docs-url
  "https://storrito.com/documentation/cli/")

(def repo-url
  "https://github.com/storrito/storrito-cli")

(defn org-uuid
  "The organization UUID of the app served at `origin`, the first label
   of its host name, e.g. `https://ORG_UUID.storrito.com` -> `ORG_UUID`.
   Nil for a host without one (a development environment on
   localhost)."
  [origin]
  (let [host (-> origin
                 (str/replace #"^https?://" "")
                 (str/replace #"[:/].*$" ""))
        label (first (str/split host #"\."))]
    (when (re-matches #"[0-9a-fA-F-]{36}" (or label ""))
      label)))

(def intro
  "The storrito command-line tool schedules Instagram Stories, Reels and TikTok posts from a terminal, or lets a coding agent such as Claude Code do it for you. Every procedure of the Storrito API is a command, the output is JSON, and you sign in through the browser instead of handling API keys.")

(def story-html
  "<insta-story>
  <div style=\"position:absolute;inset:0;background:#1e3a8a;color:white;font-family:sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:40px;padding:300px 60px;text-align:center\">
    <h1 style=\"font-size:96px;margin:0\">Hello from my terminal</h1>
    <insta-hashtag hashtag=\"storritocli\" design=\"gray\"></insta-hashtag>
  </div>
</insta-story>")

(defn steps
  "The steps to a first draft. Each step has a `:title`, a
   `:description` and one or more `:commands` (strings the page shows
   with a Copy button); `:file` is a file the user saves first."
  []
  [{:title "Install the CLI"
    :description "One self-contained executable, no root or administrator rights needed. It lands in ~/.local/bin on macOS and Linux, in %LOCALAPPDATA%\\Programs\\storrito on Windows."
    :commands [{:label "macOS and Linux" :text install-unix}
               {:label "Windows (PowerShell)" :text install-windows}]}
   {:title "Sign in"
    :description "The terminal shows a code and opens the browser. Sign in to Storrito there and confirm the code; the CLI then knows this organization. Run it again later to add another organization."
    :commands [{:text "storrito login"}
               {:text "storrito status"}]}
   {:title "Look around"
    :description "help lists the built-in commands and every API procedure, --help explains one of them with its flags."
    :commands [{:text "storrito help"}
               {:text "storrito list-instagram-users"}
               {:text "storrito schedule-instagram-story --help"}]}
   {:title "Create a draft"
    :description "Save the HTML below as story.html, then create a draft from it. Nothing is posted: the draft appears in your gallery with a preview, and the same HTML works with schedule-instagram-story, schedule-instagram-reel and schedule-tiktok-post."
    :file {:name "story.html" :text story-html}
    :commands [{:text "UUID=$(storrito generate-uuid --compact | jq -r .uuid)"}
               {:text "storrito create-draft --draftUuid $UUID --name \"My first CLI draft\" --html @story.html"}
               {:text "storrito status-draft --draftUuid $UUID"}]}
   {:title "Connect your coding agent"
    :description "Installs the Storrito skill, so the agent knows when and how to use the CLI: setup claude for Claude Code, setup codex for Codex, setup agents for both. Afterwards ask it for something like \"Create a Storrito draft from story.html\"."
    :commands [{:label "Claude Code" :text "storrito setup claude"}
               {:label "Codex" :text "storrito setup codex"}
               {:label "Both" :text "storrito setup agents"}]}])

(def agent-notes
  ["storrito commands prints every command with its description and the JSON schema of its parameters, so an agent can discover what it can do."
   "Output is JSON on stdout, errors are JSON on stderr with the details of the API response. Exit codes: 0 ok, 1 error, 2 usage, 3 not logged in, 4 the API rejected the parameters, 5 rate limited, 6 network."
   "The CLI never prompts. Without a login it stops with exit code 3 and a hint to run storrito login."
   "Requests are retried automatically on rate limits and during deployments."])

(defn unattended-commands
  "The environment for CI and agents in the cloud: an API credential
   instead of a browser login. `org-uuid` personalizes the organization."
  [org-uuid]
  [{:text (str "export STORRITO_TOKEN=<credential-id>:<credential-secret>")}
   {:text (str "export STORRITO_ORG=" (or org-uuid "<organization-uuid>"))}
   {:text "storrito list-instagram-users"}])

(def unattended-intro
  "For CI pipelines and agents that run without a browser, use an API credential instead of a login. Create one under Manage API Credentials and put it into the environment; the organization UUID is the one of this workspace.")

(def upgrade-note
  "storrito upgrade replaces the executable with the latest release from GitHub; the CLI also mentions a new version once a day when you use it in a terminal.")
